import React from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useAuth } from '../shared/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import NotificationBell from "./NotificationBell";
import GlobalSearch from './GlobalSearch';
import { useSidebar } from '../shared/SidebarContext';
import { usePermissions } from '../shared/permissions';
import './HeaderTop.css';

const HeaderTop = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { isAdmin } = usePermissions();
  const { toggleMobile } = useSidebar();

  // State for tracking photo version for cache busting
  const [photoVersion, setPhotoVersion] = React.useState(Date.now());
  const rawPhotoUrl = user?.profile?.photo;
  const photoUrl = rawPhotoUrl
    ? rawPhotoUrl.replace(/^https?:/, window.location.protocol)
    : null;

  React.useEffect(() => {
    setPhotoVersion(Date.now());
  }, [photoUrl, user]);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserAvatar = () => {
    if (photoUrl) {
      const cacheBuster = `v=${photoVersion}`;
      const fullUrl = photoUrl.includes('?')
        ? `${photoUrl}&${cacheBuster}`
        : `${photoUrl}?${cacheBuster}`;
      return <span className="avatar avatar-sm" style={{ backgroundImage: `url(${fullUrl})` }} />;
    }
    if (user?.profile?.first_name) {
      return (
        <span className="avatar avatar-sm">
          {user.profile.first_name.slice(0, 1)}{user.profile.last_name?.slice(0, 1) || ''}
        </span>
      );
    }
    if (user?.username) return <span className="avatar avatar-sm">{user.username.slice(0, 1)}</span>;
    return <span className="avatar avatar-sm">U</span>;
  };

  return (
    <header className="app-topbar d-print-none">
      {/* Mobile hamburger — only visible on small screens */}
      <button
        className="app-topbar-hamburger d-lg-none"
        onClick={toggleMobile}
        aria-label={t('components.header.toggleNavigation')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M4 6l16 0" /><path d="M4 12l16 0" /><path d="M4 18l16 0" />
        </svg>
      </button>

      {/* Global Search */}
      <div className="app-topbar-search">
        <GlobalSearch />
      </div>

      {/* Right-side actions */}
      <div className="app-topbar-actions">
        <ThemeToggle />
        <LanguageSwitcher />
        <NotificationBell />

        {user && (
          <Dropdown align="end">
            <Dropdown.Toggle
              as="a"
              href="#"
              bsPrefix="x"
              className="nav-link d-flex align-items-center gap-2 lh-1 text-reset p-0"
              aria-label="Open user menu"
              style={{ cursor: 'pointer' }}
            >
              {getUserAvatar()}
              <div className="d-none d-xl-block">
                <div className="fw-medium" style={{ fontSize: '0.8125rem' }}>{user.username || 'User'}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '1px' }}>
                  {user.role || t('components.header.userRole')}
                </div>
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-end dropdown-menu-arrow">
              <Dropdown.Item as={Link} to="/accounts/profile">{t('auth.profile')}</Dropdown.Item>
              <Dropdown.Item as={Link} to="/accounts/profile?tab=sessions">{t('profile.sessions.title')}</Dropdown.Item>
              {isAdmin && <Dropdown.Item as={Link} to="/settings">{t('menu.settings')}</Dropdown.Item>}
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout}>{t('auth.logout')}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    </header>
  );
};

export default HeaderTop;
